import { useState } from "react";
import { getApiUrl } from "../utils/api";
const API = getApiUrl();
import { useNavigate } from "react-router-dom";
import { PartyPopper, ArrowRight, ArrowLeft, Upload, Calendar, MapPin } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";


export default function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    partner1_name: "",
    partner2_name: "",
    slug: "",
    wedding_date: "",
    event_location: "",
    story: "",
    event_type: "casamento"
  });
  const [coverImage, setCoverImage] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [siteId, setSiteId] = useState(null);

  const eventTypes = [
    { value: "casamento", label: "Casamento", icon: "💍" },
    { value: "aniversario", label: "Aniversário", icon: "🎂" },
    { value: "cha-bebe", label: "Chá de Bebê", icon: "👶" },
    { value: "formatura", label: "Formatura", icon: "🎓" },
    { value: "festa", label: "Festa", icon: "🎉" },
    { value: "corporativo", label: "Evento Corporativo", icon: "🏢" },
    { value: "outro", label: "Outro", icon: "✨" }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Auto-generate slug based on event type
    if (name === 'partner1_name' || name === 'partner2_name') {
      const name1 = name === 'partner1_name' ? value : formData.partner1_name;
      const name2 = name === 'partner2_name' ? value : formData.partner2_name;
      
      if (formData.event_type === 'casamento' && name1 && name2) {
        const slug = `${name1.toLowerCase().split(' ')[0]}-e-${name2.toLowerCase().split(' ')[0]}`.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        setFormData(prev => ({ ...prev, slug }));
      } else if (name1) {
        const slug = name1.toLowerCase().split(' ')[0].normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        setFormData(prev => ({ ...prev, slug }));
      }
    }
  };

  const handleEventTypeChange = (value) => {
    setFormData(prev => ({ ...prev, event_type: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCoverImage(file);
      setCoverPreview(URL.createObjectURL(file));
    }
  };

  const createSite = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/sites`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Erro ao criar evento');
      }

      const site = await res.json();
      setSiteId(site.site_id);
      setStep(3);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const uploadCoverImage = async () => {
    if (!coverImage || !siteId) {
      navigate('/dashboard');
      return;
    }

    setLoading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', coverImage);

      const res = await fetch(`${API}/sites/${siteId}/upload-image?image_type=cover`, {
        method: 'POST',
        credentials: 'include',
        body: formDataUpload
      });

      if (!res.ok) throw new Error('Erro ao fazer upload');

      toast.success("Evento criado com sucesso!");
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.message);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getLabels = () => {
    if (formData.event_type === 'casamento') {
      return {
        title1: "Quem são os noivos?",
        subtitle1: "Digite os nomes para personalizar seu site",
        name1Label: "Nome do primeiro noivo(a)",
        name2Label: "Nome do segundo noivo(a)",
        name1Placeholder: "Ex: Maria",
        name2Placeholder: "Ex: João",
        showName2: true,
        storyLabel: "Nossa História",
        storyPlaceholder: "Conte um pouco sobre como vocês se conheceram..."
      };
    } else if (formData.event_type === 'aniversario') {
      return {
        title1: "Quem é o aniversariante?",
        subtitle1: "Digite o nome para personalizar seu site",
        name1Label: "Nome do aniversariante",
        name2Label: "",
        name1Placeholder: "Ex: Pedro",
        name2Placeholder: "",
        showName2: false,
        storyLabel: "Sobre o Aniversariante",
        storyPlaceholder: "Conte um pouco sobre o aniversariante..."
      };
    } else if (formData.event_type === 'cha-bebe') {
      return {
        title1: "Quem são os pais?",
        subtitle1: "Digite os nomes para personalizar seu site",
        name1Label: "Nome da mãe",
        name2Label: "Nome do pai (opcional)",
        name1Placeholder: "Ex: Ana",
        name2Placeholder: "Ex: Carlos",
        showName2: true,
        storyLabel: "Sobre o Bebê",
        storyPlaceholder: "Conte sobre a chegada do bebê..."
      };
    } else if (formData.event_type === 'formatura') {
      return {
        title1: "Quem é o formando?",
        subtitle1: "Digite o nome para personalizar seu site",
        name1Label: "Nome do formando(a)",
        name2Label: "",
        name1Placeholder: "Ex: Julia",
        name2Placeholder: "",
        showName2: false,
        storyLabel: "Sobre a Formatura",
        storyPlaceholder: "Conte sobre sua jornada acadêmica..."
      };
    } else {
      return {
        title1: "Quem organiza o evento?",
        subtitle1: "Digite o nome para personalizar seu site",
        name1Label: "Nome do organizador/homenageado",
        name2Label: "Segundo nome (opcional)",
        name1Placeholder: "Ex: Empresa ABC",
        name2Placeholder: "",
        showName2: true,
        storyLabel: "Sobre o Evento",
        storyPlaceholder: "Conte sobre o evento..."
      };
    }
  };

  const labels = getLabels();

  const renderStep1 = () => (
    <div className="space-y-6 animate-fade-in">
      <div className="text-center mb-8">
        <h2 className="font-serif text-3xl text-[#6E0F1A] mb-2">
          Que tipo de evento?
        </h2>
        <p className="text-[#6E0F1A]/70">
          Escolha o tipo de evento que você quer criar
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {eventTypes.map((type) => (
          <button
            key={type.value}
            type="button"
            onClick={() => handleEventTypeChange(type.value)}
            className={`p-4 rounded-xl border-2 transition-all text-center ${
              formData.event_type === type.value
                ? 'border-[#B3121B] bg-[#B3121B]/5'
                : 'border-[#F3C9CF] hover:border-[#B3121B]/50'
            }`}
          >
            <span className="text-2xl mb-2 block">{type.icon}</span>
            <span className="text-sm text-[#6E0F1A] font-medium">{type.label}</span>
          </button>
        ))}
      </div>

      <div className="pt-4">
        <h3 className="font-serif text-xl text-[#6E0F1A] mb-4 text-center">
          {labels.title1}
        </h3>
        <p className="text-[#6E0F1A]/70 text-center mb-6">
          {labels.subtitle1}
        </p>

        <div className={`grid grid-cols-1 ${labels.showName2 ? 'md:grid-cols-2' : ''} gap-6`}>
          <div>
            <Label htmlFor="partner1_name" className="text-[#6E0F1A]">{labels.name1Label}</Label>
            <Input
              id="partner1_name"
              name="partner1_name"
              value={formData.partner1_name}
              onChange={handleChange}
              placeholder={labels.name1Placeholder}
              className="input-wedding mt-2"
              data-testid="partner1-input"
            />
          </div>
          {labels.showName2 && (
            <div>
              <Label htmlFor="partner2_name" className="text-[#6E0F1A]">{labels.name2Label}</Label>
              <Input
                id="partner2_name"
                name="partner2_name"
                value={formData.partner2_name}
                onChange={handleChange}
                placeholder={labels.name2Placeholder}
                className="input-wedding mt-2"
                data-testid="partner2-input"
              />
            </div>
          )}
        </div>
      </div>

      <div>
        <Label htmlFor="slug" className="text-[#6E0F1A]">URL do seu site</Label>
        <div className="flex items-center mt-2">
          <span className="text-[#6E0F1A]/60 text-sm mr-2">fiestalab.com.br/</span>
          <Input
            id="slug"
            name="slug"
            value={formData.slug}
            onChange={handleChange}
            placeholder="meu-evento"
            className="input-wedding"
            data-testid="slug-input"
          />
        </div>
        <p className="text-xs text-[#6E0F1A]/50 mt-1">
          Use apenas letras minúsculas, números e hífens
        </p>
      </div>

      <Button
        onClick={() => setStep(2)}
        disabled={!formData.partner1_name || !formData.slug}
        className="w-full bg-[#B3121B] hover:bg-[#9A0F17] text-white rounded-full py-6 text-lg"
        data-testid="next-step-btn"
      >
        Próximo
        <ArrowRight className="w-5 h-5 ml-2" />
      </Button>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6 animate-fade-in">
      <div className="text-center mb-8">
        <h2 className="font-serif text-3xl text-[#6E0F1A] mb-2">
          Detalhes do evento
        </h2>
        <p className="text-[#6E0F1A]/70">
          Informações opcionais sobre o grande dia
        </p>
      </div>

      <div>
        <Label htmlFor="wedding_date" className="text-[#6E0F1A] flex items-center gap-2">
          <Calendar className="w-4 h-4" />
          Data do Evento
        </Label>
        <Input
          id="wedding_date"
          name="wedding_date"
          type="date"
          value={formData.wedding_date}
          onChange={handleChange}
          className="input-wedding mt-2"
          data-testid="date-input"
        />
      </div>

      <div>
        <Label htmlFor="event_location" className="text-[#6E0F1A] flex items-center gap-2">
          <MapPin className="w-4 h-4" />
          Local do Evento
        </Label>
        <Input
          id="event_location"
          name="event_location"
          value={formData.event_location}
          onChange={handleChange}
          placeholder="Ex: Salão de Festas ABC, São Paulo - SP"
          className="input-wedding mt-2"
          data-testid="location-input"
        />
      </div>

      <div>
        <Label htmlFor="story" className="text-[#6E0F1A]">{labels.storyLabel} (opcional)</Label>
        <Textarea
          id="story"
          name="story"
          value={formData.story}
          onChange={handleChange}
          placeholder={labels.storyPlaceholder}
          className="input-wedding mt-2 min-h-[120px]"
          data-testid="story-input"
        />
      </div>

      <div className="flex gap-4">
        <Button
          variant="outline"
          onClick={() => setStep(1)}
          className="flex-1 border-[#6E0F1A] text-[#6E0F1A] rounded-full py-6"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Voltar
        </Button>
        <Button
          onClick={createSite}
          disabled={loading}
          className="flex-1 bg-[#B3121B] hover:bg-[#9A0F17] text-white rounded-full py-6 text-lg"
          data-testid="create-site-btn"
        >
          {loading ? "Criando..." : "Criar Evento"}
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6 animate-fade-in">
      <div className="text-center mb-8">
        <div className="w-20 h-20 bg-[#567D50] rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="font-serif text-3xl text-[#6E0F1A] mb-2">
          Evento criado!
        </h2>
        <p className="text-[#6E0F1A]/70">
          Agora adicione uma foto de capa (opcional)
        </p>
      </div>

      <div 
        className="border-2 border-dashed border-[#F3C9CF] rounded-2xl p-8 text-center cursor-pointer hover:border-[#B3121B] transition-colors"
        onClick={() => document.getElementById('cover-upload').click()}
      >
        {coverPreview ? (
          <img src={coverPreview} alt="Preview" className="w-full h-48 object-cover rounded-lg" />
        ) : (
          <div className="py-8">
            <Upload className="w-12 h-12 text-[#B3121B] mx-auto mb-4" />
            <p className="text-[#6E0F1A]">Clique para fazer upload</p>
            <p className="text-sm text-[#6E0F1A]/50 mt-1">PNG, JPG até 10MB</p>
          </div>
        )}
        <input
          id="cover-upload"
          type="file"
          accept="image/*"
          onChange={handleImageChange}
          className="hidden"
          data-testid="cover-upload-input"
        />
      </div>

      <div className="flex gap-4">
        <Button
          variant="outline"
          onClick={() => navigate('/dashboard')}
          className="flex-1 border-[#6E0F1A] text-[#6E0F1A] rounded-full py-6"
        >
          Pular por agora
        </Button>
        <Button
          onClick={uploadCoverImage}
          disabled={loading}
          className="flex-1 bg-[#B3121B] hover:bg-[#9A0F17] text-white rounded-full py-6"
          data-testid="finish-btn"
        >
          {loading ? "Salvando..." : "Finalizar"}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA] relative">
      <div className="absolute inset-0 noise-overlay"></div>
      
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-[#F3C9CF]/30">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-center">
          <PartyPopper className="w-8 h-8 text-[#B3121B]" />
          <span className="font-cursive text-2xl text-[#6E0F1A] ml-2">Fiesta Lab</span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12 relative z-10">
        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-12">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`h-2 rounded-full transition-all ${
                s === step ? 'w-12 bg-[#B3121B]' : s < step ? 'w-8 bg-[#567D50]' : 'w-8 bg-[#F3C9CF]'
              }`}
            />
          ))}
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
        </div>
      </main>
    </div>
  );
}
